import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useOktaAuth } from "@okta/okta-react";

export interface AuthUser {
  name: string;
  email: string;
}

export interface AuthContext {
  user: AuthUser | null;
  isAuthenticated: boolean;
  getAccessToken: () => Promise<string>;
  logout: () => void;
}

export const AuthReactContext = createContext<AuthContext | null>(null);

const SANDBOX_AUTH: AuthContext = {
  user: { name: "Sandbox User", email: "sandbox@local" },
  isAuthenticated: true,
  getAccessToken: async () => "sandbox-token",
  logout: () => {},
};

export { SANDBOX_AUTH };

function claimsToUser(
  claims: Record<string, unknown> | undefined,
): AuthUser | null {
  if (!claims) return null;
  const name =
    typeof claims.name === "string"
      ? claims.name
      : typeof claims.preferred_username === "string"
        ? claims.preferred_username
        : "";
  const email = typeof claims.email === "string" ? claims.email : "";
  if (!name && !email) return null;
  return { name: name || email || "User", email };
}

const LOCAL_TOKEN_KEY = "localAdminToken";

function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const base64 = token.split(".")[1];
    return JSON.parse(atob(base64));
  } catch {
    return {};
  }
}

export function AuthStateFromOkta({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const { oktaAuth, authState } = useOktaAuth();
  const [localToken, setLocalToken] = useState<string | null>(
    () => sessionStorage.getItem(LOCAL_TOKEN_KEY),
  );

  const handleLocalLogin = useCallback((token: string) => {
    sessionStorage.setItem(LOCAL_TOKEN_KEY, token);
    setLocalToken(token);
  }, []);

  const localLogout = useCallback(() => {
    sessionStorage.removeItem(LOCAL_TOKEN_KEY);
    setLocalToken(null);
  }, []);

  const oktaGetAccessToken = useCallback(async () => {
    const token = await oktaAuth.getAccessToken();
    return token ?? "";
  }, [oktaAuth]);

  const oktaLogout = useCallback(() => {
    void oktaAuth.signOut();
  }, [oktaAuth]);

  const value = useMemo((): AuthContext & { onLocalLogin?: (token: string) => void } => {
    if (localToken) {
      const claims = decodeJwtPayload(localToken);
      const exp = typeof claims.exp === "number" ? claims.exp : 0;
      if (exp * 1000 < Date.now()) {
        sessionStorage.removeItem(LOCAL_TOKEN_KEY);
        return {
          user: null,
          isAuthenticated: false,
          getAccessToken: async () => "",
          logout: localLogout,
          onLocalLogin: handleLocalLogin,
        };
      }
      const sub = typeof claims.sub === "string" ? claims.sub : "admin";
      return {
        user: { name: sub, email: `${sub}@local` },
        isAuthenticated: true,
        getAccessToken: async () => localToken,
        logout: localLogout,
        onLocalLogin: handleLocalLogin,
      };
    }

    const idClaims = authState?.idToken?.claims as
      | Record<string, unknown>
      | undefined;
    return {
      user: authState?.isAuthenticated ? claimsToUser(idClaims) : null,
      isAuthenticated: Boolean(authState?.isAuthenticated),
      getAccessToken: oktaGetAccessToken,
      logout: oktaLogout,
      onLocalLogin: handleLocalLogin,
    };
  }, [localToken, authState, oktaGetAccessToken, oktaLogout, localLogout, handleLocalLogin]);

  return React.createElement(
    AuthReactContext.Provider,
    { value },
    children,
  );
}

export function useAuth(): AuthContext & { onLocalLogin?: (token: string) => void } {
  const ctx = useContext(AuthReactContext);
  if (!ctx) {
    throw new Error("useAuth must be used within OktaProvider");
  }
  return ctx as AuthContext & { onLocalLogin?: (token: string) => void };
}
